import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Wallet, RefreshCw, AlertTriangle, Check, X, Info, Inbox, ChevronRight, ChevronDown, BarChart3, TrendingUp, ChevronUp } from 'lucide-react';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import './BudgetsView.css';
import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import pinterestAdsLogo from '../logos/pinterest-ads.png';
import tiktokAdsLogo from '../logos/tiktok-ads.png';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';
import awinLogo from '../logos/awin.png';

/**
 * Budgets — où en est chaque compte de son enveloppe, et vers quoi le mois tend.
 *
 * ## Ce que cet écran ajoute à la vue Projection du consolidé
 *
 * La même comparaison, lue dans l'autre sens. Le consolidé répond « pour cette propriété, que
 * vaut chaque canal » ; ici on demande « pour ce parc, quel compte dérape ». Les colonnes sont
 * donc les mêmes, et délibérément : ce sont les mêmes chiffres, il n'y a aucune raison de les
 * nommer autrement d'un écran à l'autre.
 *
 * ## Les trois choses que cet écran refuse de faire
 *
 * 1. **Afficher un zéro à la place d'une absence.** Un compte dont l'API n'a pas répondu porte
 *    « indisponible », pas « 0 € ». Un zéro se lirait comme un compte à l'arrêt — c'est une tout
 *    autre nouvelle, et elle serait fausse.
 * 2. **Écrire une proposition de lui-même.** Un budget repris du consolidé s'affiche en grisé et
 *    attend un clic. Tant qu'il n'est pas accepté, la ligne n'a pas de budget.
 * 3. **Projeter le 1er du mois.** Aucune journée pleine n'est écoulée : la colonne Atterrissage
 *    reste vide, et le bandeau dit pourquoi.
 */

const LOGOS = {
  GOOGLE_ADS: googleAdsLogo,
  MICROSOFT_ADS: microsoftAdsLogo,
  FACEBOOK_ADS: metaAdsLogo,
  PINTEREST_ADS: pinterestAdsLogo,
  TIKTOK_ADS: tiktokAdsLogo,
  OPENAI_ADS: chatgptAdsLogo,
  AWIN: awinLogo
};

const NOMS_REGIE = {
  GOOGLE_ADS: 'Google Ads',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin'
};

/**
 * Pourquoi une dépense manque. Repris mot pour mot des raisons que `serieAdsQuotidienne`
 * remonte : les traduire ici, plutôt que côté serveur, garde le journal en clair.
 */
const RAISONS = {
  regie_non_prise_en_charge: 'régie non prise en charge',
  erreur_api: "l'API n'a pas répondu",
  connexion_absente: 'aucune connexion active',
  jeton_expire: 'connexion à renouveler',
  cle_illisible: 'clé d\'accès illisible',
  compte_sans_account_id: 'identifiant de compte manquant',
  compte_sans_advertiser_id: 'identifiant annonceur manquant',
  compte_sans_customer_id: 'identifiant client manquant'
};

const euro = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euroPrecis = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const pourcent = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

const formater = (valeur, precis = false) =>
  typeof valeur === 'number' ? (precis ? euroPrecis : euro).format(valeur) : '—';

/** Le libellé d'une plage, comme le consolidé l'écrit sous ses en-têtes : « 01/09 → 24/09 ». */
const libellePlage = (debut, fin) => {
  if (!debut || !fin) return '';
  const jour = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  return `${jour(debut)} → ${jour(fin)}`;
};

const libelleMois = (jour) => {
  if (!jour) return '';
  return new Date(`${jour}T00:00:00Z`)
    .toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

/**
 * La teinte de l'écart.
 *
 * Les deux couleurs sont celles du comparatif du consolidé, à l'identique — il s'agit du même
 * constat, et deux palettes diraient au lecteur qu'il regarde deux choses différentes. Le sens,
 * lui, est propre au budget : dépasser est rouge, rester en dessous est vert. Sous un demi-point
 * d'écart, aucune teinte — colorer un écart de 0,2 % lui donnerait un poids qu'il n'a pas.
 */
const teinteEcart = (ecart) => {
  if (ecart === null || Math.abs(ecart) < 0.5) return null;
  return ecart > 0
    ? { backgroundColor: '#f6b8b8', color: '#7f1d1d' }
    : { backgroundColor: '#a7d7c5', color: '#14532d' };
};

const BudgetsView = ({ token, selectedClientId, selectedSiteId }) => {
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [periode, setPeriode] = useState(null);
  const [clients, setClients] = useState([]);
  const [totaux, setTotaux] = useState({ budget: null, realise: null, atterrissage: null });
  const [comptesEnEchec, setComptesEnEchec] = useState([]);

  /**
   * Les clients dont le détail est ouvert.
   *
   * **Tout est replié au départ**, y compris quand il n'y a qu'un client : l'écran répond
   * d'abord à « où en est chaque client », et trente lignes de comptes noient cette lecture.
   * Le détail se demande, il ne s'impose pas.
   *
   * L'état est local et volontairement non mémorisé : il suit la lecture en cours, et retrouver
   * l'écran replié à chaque venue vaut mieux que d'hériter d'un dépliage oublié la veille.
   */
  const [deplies, setDeplies] = useState({});

  const basculer = (cle) => setDeplies(etat => ({ ...etat, [cle]: !etat[cle] }));

  /**
   * La colonne qui ordonne le tableau, et son sens.
   *
   * Par le nom au départ : c'est l'ordre dans lequel on cherche un client qu'on connaît déjà.
   * Les colonnes de montants s'ouvrent en **décroissant** au premier clic — on trie par
   * « réalisé » pour voir qui dépense le plus, pas le moins.
   */
  const [tri, setTri] = useState({ colonne: 'nom', sens: 'asc' });

  const trierPar = (colonne) => setTri(actuel => (
    actuel.colonne === colonne
      ? { colonne, sens: actuel.sens === 'asc' ? 'desc' : 'asc' }
      : { colonne, sens: colonne === 'nom' ? 'asc' : 'desc' }
  ));

  /** Le compte dont la cellule Budgété est ouverte à la saisie, et sa valeur en cours. */
  const [enEdition, setEnEdition] = useState(null);
  const [saisie, setSaisie] = useState('');
  const [enregistrement, setEnregistrement] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const parametres = new URLSearchParams();
      if (selectedClientId) parametres.set('clientId', selectedClientId);
      if (selectedSiteId && selectedSiteId !== 'all') parametres.set('siteId', selectedSiteId);

      const reponse = await fetch(`/api/budgets/portefeuille?${parametres}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const donnees = await reponse.json();

      if (!reponse.ok || !donnees.success) {
        throw new Error(donnees.error || 'Chargement impossible');
      }

      setPeriode(donnees.periode || null);
      setClients(donnees.clients || []);
      setTotaux(donnees.totaux || { budget: null, realise: null, atterrissage: null });
      setComptesEnEchec(donnees.comptesEnEchec || []);
    } catch (e) {
      setErreur(e.message);
      setClients([]);
    } finally {
      setChargement(false);
    }
  }, [token, selectedClientId, selectedSiteId]);

  useEffect(() => { charger(); }, [charger]);

  /**
   * Écrire un budget, ou l'effacer.
   *
   * La tolérance de saisie est celle de la grille du consolidé : virgule décimale, espaces et
   * symbole euro acceptés. Une chaîne vide n'envoie pas zéro mais `null` — le serveur supprime
   * alors la ligne, et la proposition peut reparaître.
   */
  const enregistrer = async (compte, valeurBrute) => {
    const nettoyee = String(valeurBrute).replace(/[\s€]/g, '').replace(',', '.');
    const montant = nettoyee === '' ? null : Number(nettoyee);

    if (montant !== null && (!Number.isFinite(montant) || montant < 0)) {
      setErreur('Montant invalide');
      return;
    }

    setEnregistrement(compte.id);
    try {
      const reponse = await fetch('/api/budgets/compte', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ libraryItemId: compte.id, montant })
      });
      const donnees = await reponse.json();
      if (!reponse.ok || !donnees.success) throw new Error(donnees.error || 'Enregistrement impossible');

      setEnEdition(null);
      setSaisie('');
      await charger();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnregistrement(null);
    }
  };

  const joursRestants = useMemo(
    () => (periode?.joursDuMois && periode?.joursCouverts ? periode.joursDuMois - periode.joursCouverts : 0),
    [periode]
  );

  const ecartDe = (ligne) =>
    typeof ligne.budget === 'number' && ligne.budget > 0 && typeof ligne.atterrissage === 'number'
      ? ((ligne.atterrissage - ligne.budget) / ligne.budget) * 100
      : null;

  const resteDe = (ligne) =>
    typeof ligne.budget === 'number' && typeof ligne.realise === 'number'
      ? ligne.budget - ligne.realise
      : null;

  /**
   * Trier clients et comptes selon la colonne choisie.
   *
   * ## Les vides restent en bas, dans les deux sens
   *
   * Un budget non saisi n'est pas un budget nul : le ranger avec les zéros en tri croissant, ou
   * en tête d'un tri décroissant, remplirait le haut du tableau de tirets — précisément les
   * lignes sur lesquelles il n'y a rien à lire. Elles ferment donc la marche quel que soit le
   * sens, et gardent entre elles l'ordre alphabétique, faute d'autre critère.
   *
   * ## Le même tri aux deux niveaux
   *
   * Les clients sont ordonnés par leur total, et les comptes de chaque client par le leur.
   * Trier les groupes sans trier leur contenu obligerait à relire chaque ligne dépliée pour
   * retrouver ce qu'on venait de chercher.
   */
  const valeurDeTri = (ligne, colonne) => {
    if (colonne === 'nom') return null;
    if (colonne === 'ecart') return ecartDe(ligne);
    if (colonne === 'reste') return resteDe(ligne);
    return ligne[colonne];
  };

  const trier = (lignes) => {
    const parNom = (a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr');
    if (tri.colonne === 'nom') {
      return [...lignes].sort((a, b) => (tri.sens === 'asc' ? parNom(a, b) : parNom(b, a)));
    }
    return [...lignes].sort((a, b) => {
      const va = valeurDeTri(a, tri.colonne);
      const vb = valeurDeTri(b, tri.colonne);
      const aVide = typeof va !== 'number';
      const bVide = typeof vb !== 'number';
      if (aVide && bVide) return parNom(a, b);
      if (aVide) return 1;
      if (bVide) return -1;
      return tri.sens === 'asc' ? va - vb : vb - va;
    });
  };

  const clientsTries = trier(clients);

  const nombreDeComptes = clients.reduce((total, c) => total + c.comptes.length, 0);

  /**
   * Ce qui manque pour que l'écart et le reste aient un sens.
   *
   * Les deux dernières colonnes se calculent **à partir du budgété** : sans enveloppe, il n'y a
   * rien pour rapporter l'atterrissage, et rien à soustraire. Elles restent donc vides — ce qui
   * est juste, mais illisible : deux colonnes de tirets se lisent comme une panne, pas comme une
   * saisie qui reste à faire. L'écran le dit désormais.
   *
   * Les comptes sont comptés **une fois**, par leur identifiant : un compte rattaché à deux
   * clients paraît dans deux groupes, et l'annoncer deux fois donnerait un décompte faux.
   */
  const lignesUniques = useMemo(() => {
    const parId = new Map();
    for (const client of clients) for (const compte of client.comptes) parId.set(compte.id, compte);
    return [...parId.values()];
  }, [clients]);

  const avecBudget = lignesUniques.filter(c => typeof c.budget === 'number').length;

  /**
   * L'attente est celle d'un dashboard, elle en porte donc l'écran.
   *
   * Le portefeuille interroge chaque régie compte par compte : c'est le même travail, et la même
   * durée, que le chargement d'un dashboard de plateforme. Un message à part aurait fait croire
   * à une autre nature d'attente.
   */
  if (chargement) {
    return (
      <div className="budgets-vue">
        <DashboardLoadingScreen
          platformName="publicitaires"
          features={[
            { icon: <Wallet size={28} />, label: 'Enveloppes budgétaires' },
            { icon: <BarChart3 size={28} />, label: 'Dépenses du mois' },
            { icon: <TrendingUp size={28} />, label: "Projection d'atterrissage" }
          ]}
        />
      </div>
    );
  }

  return (
    <div className="budgets-vue">
      <header className="budgets-entete">
        <div>
          <h1><Wallet size={22} /> Budgets</h1>
        </div>
        <button className="budgets-rafraichir" onClick={charger} title="Relire les dépenses">
          <RefreshCw size={15} /> Actualiser
        </button>
      </header>

      {erreur && (
        <div className="budgets-alerte budgets-alerte--erreur">
          <AlertTriangle size={16} /> {erreur}
        </div>
      )}

      {/* Le bandeau de totaux. Il porte les mêmes trois chiffres que les colonnes, à l'échelle
          du périmètre entier — c'est la première chose qu'on vient lire. */}
      <section className="budgets-totaux">
        <div className="budgets-total">
          <span className="budgets-total-haut">Budgété</span>
          <strong>{formater(totaux.budget)}</strong>
          <span className="budgets-total-bas">{libelleMois(periode?.debut)}</span>
        </div>
        <div className="budgets-total">
          <span className="budgets-total-haut">Réalisé</span>
          <strong>{formater(totaux.realise)}</strong>
          <span className="budgets-total-bas">{libellePlage(periode?.debut, periode?.fin)}</span>
        </div>
        <div className="budgets-total">
          <span className="budgets-total-haut">Atterrissage</span>
          <strong>{formater(totaux.atterrissage)}</strong>
          <span className="budgets-total-bas">{libelleMois(periode?.debut)}</span>
        </div>
        <div className="budgets-total">
          <span className="budgets-total-haut">Écart</span>
          <strong style={teinteEcart(ecartDe(totaux)) ? { color: teinteEcart(ecartDe(totaux)).color } : undefined}>
            {ecartDe(totaux) === null ? '—' : `${ecartDe(totaux) > 0 ? '+' : ''}${pourcent.format(ecartDe(totaux))} %`}
          </strong>
          <span className="budgets-total-bas">
            {totaux.budget === null ? 'sans budgété, rien à rapporter' : 'atterrissage / budgété'}
          </span>
        </div>
      </section>

      {/* La projection est une hypothèse, et l'écran le dit plutôt que de la laisser passer pour
          une mesure. Le 1er du mois, il n'y en a pas du tout. */}
      <p className="budgets-note">
        <Info size={14} />
        {periode?.impossible ? (
          <span>
            Aucune journée pleine n'est encore écoulée ce mois-ci : il n'y a rien à extrapoler.
            L'atterrissage sera disponible demain.
          </span>
        ) : (
          <span>
            {periode?.joursCouverts} jour{periode?.joursCouverts > 1 ? 's' : ''} sur {periode?.joursDuMois} —
            l'atterrissage suppose que le reste du mois ressemble à ce qui est écoulé. Le budgété est
            l'enveloppe du mois entier ; le réalisé s'arrête au dernier jour couvert.
          </span>
        )}
      </p>

      {/* Pourquoi deux colonnes sont vides. Tant qu'aucune enveloppe n'est posée, l'écart et le
          reste n'ont rien à mesurer : le dire vaut mieux que de laisser deviner. */}
      {nombreDeComptes > 0 && avecBudget === 0 && (
        <div className="budgets-alerte budgets-alerte--amorce">
          <Wallet size={16} />
          <div>
            <strong>Aucune enveloppe saisie pour l'instant.</strong>
            <span>
              {' '}L'écart et le reste se calculent à partir du budgété : ils resteront vides tant
              qu'aucun montant ne sera posé. Dépliez un client, puis cliquez sur la colonne
              <em> Budgété</em> d'un de ses comptes pour saisir la sienne.
            </span>
          </div>
        </div>
      )}

      {nombreDeComptes === 0 ? (
        <div className="budgets-vide">
          <Inbox size={26} />
          <p>Aucun compte publicitaire dans ce périmètre.</p>
        </div>
      ) : (
        <div className="budgets-tableau">
          {/* Chaque en-tête ordonne le tableau. Toute la cellule est cliquable, chevron
              compris : une cible de la taille d'une icône se rate. */}
          <div className="budgets-ligne budgets-ligne--entete">
            {[
              { cle: 'nom', titre: 'Client', bas: null, libelle: true },
              { cle: 'budget', titre: 'Budgété', bas: libelleMois(periode?.debut) },
              { cle: 'realise', titre: 'Réalisé', bas: libellePlage(periode?.debut, periode?.fin) },
              { cle: 'atterrissage', titre: 'Atterrissage', bas: libelleMois(periode?.debut) },
              { cle: 'ecart', titre: 'Écart', bas: 'atterrissage / budgété' },
              { cle: 'reste', titre: 'Reste', bas: joursRestants > 0 ? `${joursRestants} j restants` : 'mois couvert' }
            ].map(colonne => (
              <button
                key={colonne.cle}
                type="button"
                onClick={() => trierPar(colonne.cle)}
                className={`budgets-cellule budgets-cellule--triable${colonne.libelle ? ' budgets-cellule--libelle' : ''}${tri.colonne === colonne.cle ? ' est-triee' : ''}`}
                title={`Trier par ${colonne.titre.toLowerCase()}`}
              >
                <span className="budgets-entete-titre">
                  {colonne.titre}
                  {tri.colonne === colonne.cle && (
                    tri.sens === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                  )}
                </span>
                {colonne.bas && <small>{colonne.bas}</small>}
              </button>
            ))}
          </div>

          {clientsTries.map(client => {
            const cle = client.id || '__sans_client__';
            const ouvert = Boolean(deplies[cle]);

            return (
            <div key={cle} className="budgets-groupe">
              {/* La ligne de client est l'interrupteur de son détail : tout y est cliquable, et
                  non le seul chevron — une cible de douze pixels se rate. */}
              <div
                className="budgets-ligne budgets-ligne--client"
                onClick={() => basculer(cle)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculer(cle); } }}
                title={ouvert ? 'Replier le détail par compte' : 'Déplier le détail par compte'}
              >
                <div className="budgets-cellule budgets-cellule--libelle">
                  <span className="budgets-chevron">
                    {ouvert ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </span>
                  {client.nom}
                  <span className="budgets-compteur">
                    {client.comptes.length} compte{client.comptes.length > 1 ? 's' : ''}
                  </span>
                  {/**
                    * L'écart d'un groupe partiellement budgété oppose l'atterrissage de tous ses
                    * comptes à l'enveloppe de quelques-uns : il paraît alors meilleur qu'il n'est.
                    * Le chiffre reste juste — c'est bien ce qui est dépensé face à ce qui est
                    * prévu — mais il ne se lit pas comme un verdict, et la ligne le dit.
                    */}
                  {(() => {
                    const sans = client.comptes.filter(c => typeof c.budget !== 'number').length;
                    if (sans === 0 || client.budget === null) return null;
                    return (
                      <span
                        className="budgets-marque budgets-marque--partiel"
                        title={`${sans} compte${sans > 1 ? 's' : ''} de ce client n'${sans > 1 ? 'ont' : 'a'} pas d'enveloppe. Leur dépense entre dans l'atterrissage, mais pas dans le budgété : l'écart de cette ligne est donc plus favorable qu'il ne le sera une fois tout saisi.`}
                      >
                        {sans} sans enveloppe
                      </span>
                    );
                  })()}
                </div>
                <div className="budgets-cellule">{formater(client.budget)}</div>
                <div className="budgets-cellule">{formater(client.realise)}</div>
                <div className="budgets-cellule">{formater(client.atterrissage)}</div>
                <div className="budgets-cellule" style={teinteEcart(ecartDe(client)) || undefined}>
                  {ecartDe(client) === null ? '—' : `${ecartDe(client) > 0 ? '+' : ''}${pourcent.format(ecartDe(client))} %`}
                </div>
                <div className="budgets-cellule">{formater(resteDe(client))}</div>
              </div>

              {ouvert && trier(client.comptes).map(compte => {
                const ecart = ecartDe(compte);
                const reste = resteDe(compte);
                const teinte = teinteEcart(ecart);
                const parJour = reste > 0 && joursRestants > 0 ? reste / joursRestants : null;

                return (
                  <div key={`${cle}-${compte.id}`} className="budgets-ligne budgets-ligne--compte">
                    <div className="budgets-cellule budgets-cellule--libelle">
                      {LOGOS[compte.type] && (
                        <img src={LOGOS[compte.type]} alt={NOMS_REGIE[compte.type] || compte.type} />
                      )}
                      <span className="budgets-nom" title={compte.nom}>{compte.nom}</span>
                      {compte.multiClient && (
                        <span
                          className="budgets-marque"
                          title="Ce compte est rattaché à plusieurs clients. Il n'est compté qu'une fois dans le total général."
                        >
                          multi-client
                        </span>
                      )}
                    </div>

                    {/* Le budgété : la seule cellule qui s'écrit. */}
                    <div className="budgets-cellule budgets-cellule--saisie">
                      {enEdition === compte.id ? (
                        <div className="budgets-edition">
                          <input
                            autoFocus
                            value={saisie}
                            onChange={e => setSaisie(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') enregistrer(compte, saisie);
                              if (e.key === 'Escape') { setEnEdition(null); setSaisie(''); }
                            }}
                            placeholder="—"
                          />
                          <button
                            title="Enregistrer"
                            disabled={enregistrement === compte.id}
                            onClick={() => enregistrer(compte, saisie)}
                          ><Check size={13} /></button>
                          <button
                            title="Annuler"
                            onClick={() => { setEnEdition(null); setSaisie(''); }}
                          ><X size={13} /></button>
                        </div>
                      ) : (
                        <button
                          className="budgets-montant"
                          onClick={() => {
                            setEnEdition(compte.id);
                            setSaisie(typeof compte.budget === 'number' ? String(compte.budget) : '');
                          }}
                          title="Modifier le budget de ce compte"
                        >
                          {typeof compte.budget === 'number'
                            ? formater(compte.budget)
                            : <Proposition proposition={compte.proposition} onAccepter={m => enregistrer(compte, m)} />}
                        </button>
                      )}
                    </div>

                    <div className="budgets-cellule">
                      {compte.indisponible
                        ? <span className="budgets-indisponible" title={raisonDe(compte, comptesEnEchec)}>indisponible</span>
                        : formater(compte.realise, true)}
                    </div>
                    <div className="budgets-cellule">{formater(compte.atterrissage)}</div>
                    <div className="budgets-cellule" style={teinte || undefined}>
                      {ecart === null ? '—' : `${ecart > 0 ? '+' : ''}${pourcent.format(ecart)} %`}
                    </div>
                    <div className="budgets-cellule">
                      {formater(reste)}
                      {parJour !== null && <small>{euro.format(parJour)} / jour</small>}
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      )}

      {/* Les comptes muets sont nommés, pas tus. Leur absence des totaux se voit alors comme un
          incident à régler, et non comme un parc qui ne dépense rien. */}
      {comptesEnEchec.length > 0 && (
        <div className="budgets-alerte budgets-alerte--echecs">
          <AlertTriangle size={16} />
          <div>
            <strong>
              {comptesEnEchec.length} compte{comptesEnEchec.length > 1 ? 's' : ''} sans dépense lisible
            </strong>
            <span> — écarté{comptesEnEchec.length > 1 ? 's' : ''} des totaux plutôt que compté{comptesEnEchec.length > 1 ? 's' : ''} pour zéro :{' '}
              {comptesEnEchec.map(c => `${c.accountName} (${RAISONS[c.raison] || c.raison})`).join(', ')}.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/** La raison pour laquelle un compte n'a pas rendu sa dépense, telle qu'on la met en infobulle. */
const raisonDe = (compte, comptesEnEchec) => {
  const echec = comptesEnEchec.find(c => c.accountId === compte.id);
  if (!echec) return 'Dépense indisponible';
  return `Dépense indisponible : ${RAISONS[echec.raison] || echec.raison}${echec.detail ? ` — ${echec.detail}` : ''}`;
};

/**
 * Ce que le consolidé propose pour ce compte, quand rien n'y a encore été saisi.
 *
 * Trois états, et un seul se clique. La distinction n'est pas cosmétique : accepter un montant
 * « à répartir » reviendrait à attribuer à un compte l'enveloppe de deux, et le portefeuille
 * afficherait alors le double du budget réel.
 */
const Proposition = ({ proposition, onAccepter }) => {
  // Sans proposition, la cellule doit **dire qu'elle s'écrit**. Un tiret y était muet : à côté
  // d'un écart et d'un reste eux aussi vides — puisqu'ils en découlent —, il se lisait comme une
  // donnée manquante, et non comme une enveloppe qui reste à poser.
  if (!proposition) return <span className="budgets-a-saisir">saisir</span>;

  if (proposition.motif === 'ferme') {
    return (
      <span
        className="budgets-proposition budgets-proposition--ferme"
        onClick={e => { e.stopPropagation(); onAccepter(proposition.montant); }}
        title="Budget saisi dans le dashboard consolidé pour cette destination. Cliquez pour l'adopter ici."
      >
        {euro.format(proposition.montant)} <em>proposé</em>
      </span>
    );
  }

  if (proposition.motif === 'a_repartir') {
    return (
      <span
        className="budgets-proposition"
        title={`Le consolidé budgète ${euro.format(proposition.enveloppe)} pour cette destination, mais ${proposition.comptes} comptes la partagent. Répartir au prorata de la dépense ferait passer un calcul pour une saisie : indiquez vous-même la part de ce compte.`}
      >
        à répartir entre {proposition.comptes}
      </span>
    );
  }

  return (
    <span
      className="budgets-proposition"
      title="Plusieurs propriétés du consolidé budgètent ce compte. Les additionner risquerait de compter deux fois le même achat : indiquez le montant qui vaut ici."
    >
      plusieurs sources
    </span>
  );
};

export default BudgetsView;
